import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SearchX } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import type { TrackedReport } from '../../../types/whs';
import { StatusBadge, SeverityBadge, REPORT_TYPE_LABEL, REPORT_STATUS_LABEL } from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatDateTimeInTz } from '../../../utils/timezone';

const inputCls =
  'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none';

export default function WhsTrackReport() {
  const orgTz = useOrgTimezone();
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [report, setReport] = useState<TrackedReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const runLookup = useCallback(async (lookupCode: string) => {
    const trimmed = lookupCode.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    const res = await whsService.trackReport(trimmed);
    if (res.success) setReport(res.data);
    else { setReport(null); setError(res.error.message); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const initial = searchParams.get('code');
    if (initial) {
      setCode(initial);
      runLookup(initial);
    }
  }, [searchParams, runLookup]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    runLookup(code);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Track a safety report"
        subtitle="Enter your tracking code to check the status of a report you submitted"
        icon={<Search className="h-6 w-6 text-white" />}
      />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="mb-6 flex flex-col sm:flex-row gap-3">
          <input
            className={inputCls}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter tracking code (e.g. WHS-A1B2C3)"
            aria-label="Tracking code"
          />
          <Button type="submit" isLoading={loading} disabled={loading} className="shrink-0">
            <Search className="h-4 w-4" /> Check status
          </Button>
        </form>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {searched && !loading && !report && !error && (
          <div className="rounded-xl border border-neutral-200 bg-white p-8 text-center">
            <SearchX className="mx-auto h-10 w-10 text-neutral-300" />
            <p className="mt-3 text-sm font-medium text-neutral-700">No report found for that code</p>
            <p className="mt-1 text-sm text-neutral-500">
              Double-check the tracking code you were given when you submitted your report.
            </p>
          </div>
        )}

        {report && (
          <div className="rounded-xl border border-neutral-200 bg-white p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-mono text-xs text-neutral-400">{report.tracking_code}</p>
                <h2 className="mt-0.5 text-lg font-semibold text-neutral-900">{report.title}</h2>
                <p className="text-sm text-neutral-500">{REPORT_TYPE_LABEL[report.report_type]}</p>
              </div>
              <div className="flex items-center gap-2">
                <SeverityBadge severity={report.severity} />
                <StatusBadge status={report.status} />
              </div>
            </div>

            <div className="mt-4 text-sm text-neutral-600">
              Reported: <span className="font-medium text-neutral-900">{formatDateTimeInTz(report.reported_at, orgTz)}</span>
            </div>

            <div className="mt-6">
              <h3 className="text-sm font-semibold text-neutral-900 mb-3">Progress updates</h3>
              {report.updates.length > 0 ? (
                <ol className="space-y-3">
                  {report.updates.map((u, i) => (
                    <li key={i} className="border-l-2 border-neutral-200 pl-4">
                      <div className="flex flex-wrap items-center gap-2">
                        {u.new_status && (
                          <span className="text-xs font-medium text-neutral-500">
                            Status: {REPORT_STATUS_LABEL[u.new_status as keyof typeof REPORT_STATUS_LABEL] ?? u.new_status}
                          </span>
                        )}
                        <span className="text-xs text-neutral-400">{formatDateTimeInTz(u.created_at, orgTz)}</span>
                      </div>
                      {u.body && <p className="mt-1 text-sm text-neutral-700">{u.body}</p>}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm text-neutral-400">No visible updates yet. Check back later.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
